import { Global, css } from '@emotion/react';

import { BDSColor } from '../tokens/color';
import { deviceSizes } from '../tokens/size';

const generateCSSVariables = (colors: object, prefix = '--'): string => {
  const variables: string[] = [];

  const traverse = (obj: object, path: string[]) => {
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object') {
        traverse(value, [...path, key]);
      } else {
        const variableName = `${prefix}${path.concat(key).join('-')}`;
        variables.push(`${variableName}: ${value};`);
      }
    }
  };

  traverse(colors, []);
  return variables.join('\n');
};

// CSS 변수를 :root에 적용하는 글로벌 스타일 정의
const cssVariables = generateCSSVariables(BDSColor);

/**
 * 가로 캐러셀 양끝 페이드의 스톱 [위치%, 불투명도].
 * 투명 → 배경색으로 곧장 잇지 않고 Figma easing을 근사한 다단 스톱을 쓴다.
 */
const EDGE_FADE_STOPS: readonly (readonly [number, number])[] = [
  [0, 0],
  [4.75, 0.01],
  [9.49, 0.04],
  [14.24, 0.08],
  [18.98, 0.15],
  [23.73, 0.23],
  [28.47, 0.33],
  [33.22, 0.44],
  [37.96, 0.56],
  [42.71, 0.67],
  [47.45, 0.77],
  [52.2, 0.85],
  [56.94, 0.92],
  [61.69, 0.96],
  [66.43, 0.99],
  [71.18, 1],
];

/**
 * 페이드는 항상 페이지 배경으로 수렴해야 하므로 색 리터럴 대신 --body-background를 섞는다.
 * (예전엔 #111114 / #fff를 JS에서 isDarkMode로 분기해 박아뒀는데, 리브랜딩으로 배경이
 *  브랜드×모드별 v2 토큰이 되면서 실제 배경과 어긋났다.)
 */
const buildEdgeFade = (deg: number) =>
  `linear-gradient(${deg}deg, ${EDGE_FADE_STOPS.map(
    ([position, opacity]) =>
      `color-mix(in srgb, var(--body-background) ${+(opacity * 100).toFixed(2)}%, transparent) ${position}%`
  ).join(', ')})`;

export const ChatGlobalStyle = () => (
  <Global
    styles={css`
      :root {
        ${cssVariables}
      }

      html {
        /* iOS Safari/PWA: 텍스트 자동 확대(font boosting) 차단.
           긴 문단 블록에서 iOS가 임의 배율로 글자를 키웠다가 레이아웃 변화(스트리밍, 키보드,
           설정 저장 리렌더) 시 배율을 재계산하면서 "글자 크기가 저절로 바뀌는" 현상의 원인이 된다.
           지정한 font-size 그대로 렌더되도록 고정한다. */
        -webkit-text-size-adjust: 100%;
        text-size-adjust: 100%;
      }

      /* 모바일(바텀 네비가 노출되는 뷰포트): 문서 스크롤바를 시각적으로만 숨긴다(스크롤 동작은 유지).
         전역 GlobalStyle의 스크롤바 폭 지정(::-webkit-scrollbar width 6px)이 선택자 제한 없이 적용되면서
         모바일에서 자동 숨김(오버레이)이던 문서 스크롤바가 상시 표시형이 되어,
         화면 우측 스크롤바가 하단 고정 바텀 네비 위를 가로질러 부자연스러웠다.
         (html/body = 실제 스크롤 주체 — SContentWrapper엔 overflow가 없어 문서가 스크롤된다) */
      @media screen and (max-width: ${deviceSizes.medium}px) {
        html,
        body {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* legacy Edge */

          &::-webkit-scrollbar {
            display: none; /* Chrome/Safari */
          }
        }
      }

      /* 라이트/다크 전환 시 --v2-* 팔레트가 한 번에 교체되는데, transition이 걸린 속성만
         이전 색에서 서서히 따라와 이미 바뀐 주변과 어긋나 깜빡여 보인다
         (측정: transition 없는 요소는 2프레임 만에 최종색 도달, background 0.1s는 9프레임, 0.2s는 14프레임 이후까지 중간색).
         useDarkModeStore가 전환 시작 시 이 클래스를 걸고, 팔레트 교체를 담당하는 V2BrandVarsGlobal이
         새 색을 확정시킨 뒤 뗀다 — 그래서 hover/토글 애니메이션은 그대로 살아있다.
         클래스는 모드 클래스와 같이 html·body 양쪽에 걸린다 — html 이 캔버스 배경을 칠하므로
         아래 html, body 블록에 transition 이 생겨도 html 쪽이 안전망을 비껴가지 않게. */
      html.theme-switching,
      html.theme-switching *,
      html.theme-switching *::before,
      html.theme-switching *::after,
      body.theme-switching,
      body.theme-switching *,
      body.theme-switching *::before,
      body.theme-switching *::after {
        transition: none !important;
      }

      html,
      body {
        /* 가로 캐러셀 양끝 페이드. var() 치환은 사용하는 요소에서 일어나므로
           --body-background(.light/.dark에 정의)가 그대로 반영된다. */
        --edge-fade-right: ${buildEdgeFade(90)};
        --edge-fade-left: ${buildEdgeFade(270)};

        /* body 배경 = v2 page-bg(브랜드별로 갈리는 파생 변수).
           홈 개편 시안(팅글 884:79607)의 페이지 배경이 elevated인데, 홈에서만 갈아끼우면
           페이지를 오갈 때 배경이 바뀌어 보여 전 화면을 통일했다(2026-08-12 결정).
           다만 elevated를 두 브랜드에 공통 적용하면 리브랜딩 대상이 아닌 버블챗까지
           다크 배경이 #131415 → #242526으로 밝아진다. 그래서 page-bg가 팅글은 elevated,
           버블챗 브라우저는 기존 primary를 가리킨다. 버블챗 인앱만 네이티브와 같은 secondary(#242526)다
           (cssVars.ts V2Surface).
           fallback은 V2BrandVarsGlobal 마운트 전 한 프레임을 막기 위한 기존 하드코딩 값.
           인앱 버블챗은 fallback(#111114)과 최종값(#242526)이 한 단 다르지만, 이 규칙을 만드는
           ChatGlobalStyle 과 변수를 주입하는 V2BrandVarsGlobal 이 같은 React 커밋에 들어가 fallback 이
           실제로 그려지는 프레임은 없다. 첫 페인트 전 캔버스는 셸이 주입한 색(iOS shellBackgroundHex)이고,
           그 값을 페이지에 맞추는 건 iOS 쪽 과제다.

           이 규칙은 html 에도 매칭돼야 한다 — 이유는 useDarkModeStore 의 applyRootModeClass 주석 참조.
           (요약: #root 가 absolute 라 body 높이가 0 이고, 셸이 html 을 칠하면 캔버스 전파가 끊긴다) */
        &.light {
          --body-background: var(--v2-page-bg, #fff);
          color: #111114;
          background: var(--body-background);
        }

        &.dark {
          --body-background: var(--v2-page-bg, #111114);
          color: #fff;
          background: var(--body-background);

          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-text-fill-color: #fff;
            -webkit-box-shadow: 0 0 0px 1000px #262626 inset !important;
          }
        }
      }
    `}
  />
);
